import { useCallback, useDeferredValue, useMemo, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Combobox, DropdownButton } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { SELECT_ALL_ITEM_ID } from "app/pages/sales-intelligence/constants/common";

import { ScrollDismissBoundary } from "../../scroll-dismiss/ScrollDismissBoundary";
import { StyledBaseDropdownButtonWrap } from "../../styles";
import type { IGenericDropDownItem } from "../types";

const EMPTY_ITEMS: readonly IGenericDropDownItem<never>[] = [];
const EMPTY_SELECTION: readonly never[] = [] as const;

type ComboboxWithSelectAllProps<T extends string | number> = {
    items: readonly IGenericDropDownItem<T>[];
    buttonText: string;
    hasSearch?: boolean;
    searchPlaceHolder?: string;
    selectedItems: readonly T[] | undefined;
    selectAllLabel: string;
    disabled?: boolean;
    dataAutomation?: string;
    onSelectionChange: (items: readonly T[], isAllSelected: boolean) => void;
};

const ComboboxWithSelectAll = <T extends string | number>({
    items = EMPTY_ITEMS as readonly IGenericDropDownItem<T>[],
    selectedItems = EMPTY_SELECTION as readonly T[],
    onSelectionChange,
    selectAllLabel,
    buttonText,
    hasSearch = false,
    searchPlaceHolder,
    disabled = false,
    dataAutomation,
}: ComboboxWithSelectAllProps<T>) => {
    const { translate } = useTranslate();
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const deferredSearchTerm = useDeferredValue(searchTerm);

    if (disabled && isOpen) {
        setIsOpen(false);
        setSearchTerm("");
    }

    const hasMultipleItems = items.length > 1;
    const allIds = useMemo(() => items.map((item) => item.id), [items]);
    const isAllSelected = selectedItems.length === allIds.length;
    const isPartiallySelected = selectedItems.length > 0 && selectedItems.length < allIds.length;

    const filteredItems = useMemo(() => {
        const term = deferredSearchTerm.trim().toLowerCase();
        if (!term) {
            return items;
        }

        return items.filter((item) => item.text?.toLowerCase().includes(term));
    }, [items, deferredSearchTerm]);

    const handleOpenChange = useCallback(
        (open: boolean) => {
            if (disabled && open) {
                return;
            }

            setIsOpen(open);
            if (!open) {
                setSearchTerm("");
            }
        },
        [disabled],
    );

    const handleSelectAll = useCallback(() => {
        onSelectionChange(isAllSelected ? [] : [...allIds], true);
    }, [isAllSelected, allIds, onSelectionChange]);

    const handleSelect = useCallback(
        (itemId: T) => {
            const isSelected = selectedItems.includes(itemId);
            const newSelected = isSelected ? selectedItems.filter((id) => id !== itemId) : [...selectedItems, itemId];
            onSelectionChange(newSelected, false);
        },
        [selectedItems, onSelectionChange],
    );

    return (
        <ScrollDismissBoundary
            isOpen={isOpen}
            onClose={() => handleOpenChange(false)}
        >
            <Combobox
                isOpen={isOpen}
                onOpenChange={handleOpenChange}
                placement="bottom-start"
                data-automation={dataAutomation}
            >
                <Combobox.Trigger>
                    <StyledBaseDropdownButtonWrap disabled={disabled}>
                        <DropdownButton disabled={disabled}>{buttonText}</DropdownButton>
                    </StyledBaseDropdownButtonWrap>
                </Combobox.Trigger>

                {hasSearch ? (
                    <Combobox.SearchInput
                        value={searchTerm}
                        onChange={setSearchTerm}
                        placeholder={searchPlaceHolder}
                    />
                ) : null}

                <Combobox.Content
                    emptyState={<Combobox.OptionLabel>{translate("dropdown.no.results")}</Combobox.OptionLabel>}
                >
                    {hasMultipleItems && !deferredSearchTerm ? (
                        <Combobox.Option
                            key={SELECT_ALL_ITEM_ID}
                            id={SELECT_ALL_ITEM_ID}
                            textValue={selectAllLabel}
                            onClick={handleSelectAll}
                        >
                            <Flex
                                alignItems="center"
                                justifyContent="space-between"
                                gap="8px"
                                width="100%"
                            >
                                <Combobox.OptionLabel>{selectAllLabel}</Combobox.OptionLabel>
                                <Combobox.OptionCheckbox
                                    isChecked={isAllSelected}
                                    halfSelected={isPartiallySelected}
                                />
                            </Flex>
                        </Combobox.Option>
                    ) : null}

                    {filteredItems.map((item) => (
                        <Combobox.Option
                            key={String(item.id)}
                            id={String(item.id)}
                            textValue={item.text ?? ""}
                            onClick={() => handleSelect(item.id)}
                        >
                            <Flex
                                alignItems="center"
                                justifyContent="space-between"
                                gap="8px"
                                width="100%"
                            >
                                <Combobox.OptionLabel>{item.text}</Combobox.OptionLabel>
                                <Combobox.OptionCheckbox isChecked={selectedItems.includes(item.id)} />
                            </Flex>
                        </Combobox.Option>
                    ))}
                </Combobox.Content>
            </Combobox>
        </ScrollDismissBoundary>
    );
};

export default ComboboxWithSelectAll;
